'use client';

import { defineMessages, useIntl } from 'react-intl';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { PriceDiscountLocalized } from '@packages/modules/src/Price/PriceDiscountLocalized/PriceDiscountLocalized';
import { PriceUnitPrice } from '@packages/shared/src/ui/Price/PriceUnitPrice/PriceUnitPrice';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useFormatDiscount } from '@packages/shared/src/hooks/useFormatDiscount/useFormatDiscount';

import type { PriceProps } from '@packages/shared/src/ui/Price/Price';
import type {
  LineItemFragmentFragment,
  WishlistLineItemsFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import type { LineItemNormalizedPrice } from './LineItemTypes';

interface LineItemPriceProps extends PriceProps {
  price: WishlistLineItemsFragmentFragment['price'] | LineItemNormalizedPrice;
  discount?: LineItemFragmentFragment['price']['discount'];
}

const messages = defineMessages({
  uvp: {
    defaultMessage: 'UVP:',
    id: 'ProductPriceWrapper.uvp',
  },
  saving: {
    defaultMessage: 'statt',
    id: 'ProductPriceWrapper.saving',
  },
  startPrice: {
    id: 'price.start.from',
    defaultMessage: 'ab',
  },
});

export const LineItemPrice = ({
  alignment = 'right',
  price,
  discount,
  ...rest
}: LineItemPriceProps) => {
  const { formatMessage } = useIntl();
  const { formatPrice } = useFormatPrice();
  const { formatDiscount } = useFormatDiscount();

  if (!price) {
    return null;
  }

  const { currency, saving, valueOld, uvp, unit, valueUnit, value, isStartPrice } = price;
  const { type: savingType, value: savingValue } = saving || {};

  let preDiscountText: string | undefined;
  if (uvp) {
    preDiscountText = formatMessage(messages.uvp);
  } else if (saving) {
    preDiscountText = formatMessage(messages.saving);
  }

  // old implementation uses saving, unsure whether discount could be used instead
  let discountText: string | undefined;
  if (!savingValue) {
    discountText = undefined;
  } else if (savingType?.toLowerCase() === 'currency') {
    discountText = `-${formatDiscount({
      priceDifference: savingValue,
      currency,
    })}`;
  } else {
    discountText = `-${formatDiscount({
      discountPercentage: savingValue,
    })}`;
  }

  return (
    <Price
      className="self-stretch whitespace-nowrap md:self-start"
      alignment={alignment}
      size="small"
      {...rest}
    >
      <>
        {valueOld && (
          <div className="mb-1">
            <PriceDiscountLocalized
              oldPrice={`${preDiscountText} ${formatPrice(valueOld, currency)}`}
              oldPriceType={uvp ? 'msrp' : undefined}
              discount={discountText}
            />
          </div>
        )}
        {/* Voucher discount, not available in cart and current GraphQL schema, will be added later
          <PriceVoucherDiscount
              preVoucherPrice="999,99 €"
              savingValue="-99,99 €"
          >
              10% Discount
          </PriceVoucherDiscount>
        */}
        {value && (
          <div className="mb-1">
            <PriceValue>
              {isStartPrice && (
                <span className="text-size-body-sm mr-1">{formatMessage(messages.startPrice)}</span>
              )}
              <span className="">{formatPrice(value, currency)}</span>
            </PriceValue>
          </div>
        )}
        {unit && valueUnit && (
          <PriceUnitPrice>{`${formatPrice(valueUnit, currency)} / ${unit}`}</PriceUnitPrice>
        )}
      </>
    </Price>
  );
};
